import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_URLS } from "../config";
import { apiRequest } from "../utils/apiRequest";

const AuthContext = createContext();
const ACCESS_URL = `${API_URLS.ACCESS}/me`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [allowedPages, setAllowedPages] = useState(null); // null = not loaded yet
  const [allowedApis, setAllowedApis] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // ── Fetch the user's own access doc from backend ──────────────────────────
  const fetchMyAccess = useCallback(async (authToken) => {
    if (!authToken) return;
    // apiRequest silently refreshes an expired access token and retries;
    // it only clears the session (and returns null) if the refresh token
    // itself is invalid/expired.
    const res = await apiRequest(ACCESS_URL, {}, authToken);
    if (!res) {
      setToken(null); setUser(null); setAllowedPages([]); setAllowedApis([]);
      return;
    }
    if (res.ok) {
      setAllowedPages(res.data.pages || []);
      setAllowedApis(res.data.apis || []);
      // Persist for next reload
      localStorage.setItem("allowedPages", JSON.stringify(res.data.pages || []));
      localStorage.setItem("allowedApis", JSON.stringify(res.data.apis || []));
    } else {
      // Network/other error — use cached values if available
      const cached = localStorage.getItem("allowedPages");
      if (cached) setAllowedPages(JSON.parse(cached));
    }
  }, []);

  // ── Stay in sync when apiRequest silently refreshes the access token ──────
  useEffect(() => {
    const onTokenRefreshed = (e) => setToken(e.detail.accessToken);
    window.addEventListener("auth:token-refreshed", onTokenRefreshed);
    return () => window.removeEventListener("auth:token-refreshed", onTokenRefreshed);
  }, []);

  // ── On mount: restore user + access from localStorage, then re-fetch ──────
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedPages = localStorage.getItem("allowedPages");
    const storedApis = localStorage.getItem("allowedApis");

    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
      // Use cached access immediately so UI doesn't flash
      if (storedPages) setAllowedPages(JSON.parse(storedPages));
      if (storedApis) setAllowedApis(JSON.parse(storedApis));
      // Then re-fetch fresh access in background
      fetchMyAccess(token).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Login: store token + user, fetch access immediately ──────────────────
  const login = async (newToken, newRefreshToken, userData) => {
    localStorage.setItem("token", newToken);
    if (newRefreshToken) localStorage.setItem("refreshToken", newRefreshToken);
    localStorage.setItem("user", JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
    // Fetch access before navigating so sidebar renders correctly
    await fetchMyAccess(newToken);
  };

  // ── Logout: clear everything ──────────────────────────────────────────────
  const logout = () => {
    ["token", "user", "refreshToken", "allowedPages", "allowedApis"].forEach(k => localStorage.removeItem(k));
    setToken(null); setUser(null); setAllowedPages([]); setAllowedApis([]);
    navigate("/");
  };



  // ── Helper: check if a page key is accessible ─────────────────────────────

  const isGlobalSuperAdmin = (u) => (u || user)?.role === "global_superadmin";
  const canAccessPage = (pageKey) => {
    if (!pageKey) return true;
    if (!user) return false;
    // Global Superadmin always has full access
    if (isGlobalSuperAdmin()) return true;
    // While access is loading, block to prevent flash
    if (allowedPages === null) return false;
    return allowedPages.includes(pageKey);
  };

  // ── Helper: check if an API key is accessible ─────────────────────────────
  const canCallApi = (apiKey) => {
    if (!apiKey) return true;
    if (!user) return false;
    if (isGlobalSuperAdmin()) return true;
    return allowedApis.includes(apiKey);
  };

  // ── Refresh access (call after Access Panel saves) ────────────────────────
  const refreshAccess = () => fetchMyAccess(token);

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      allowedPages, allowedApis,
      login, logout,
      canAccessPage, canCallApi,
      refreshAccess,
      isGlobalSuperAdmin: isGlobalSuperAdmin(),
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};